'use strict';

const libPictView = require('pict-view');

/**
 * FeatureBoard -- the features board, and the place a feature's promise is judged.
 *
 * WHY IT IS NOT THE WORK ITEM BOARD. Work items move at agent speed; features move at product
 * speed. Putting both on one surface implies they should move together, and that implication is
 * the pressure that produces rubber stamps. So features got their own board and came off the
 * other one.
 *
 * WHAT IS ON A CARD, AND WHY. Work items connected, how many are Done, the commits and deploys
 * behind them, when the last work item landed, whether a connected item is still bouncing off
 * review, and how many times this feature has already been accepted (its rebuild history, made
 * visible without opening anything). A card that carried only a name would leave a reader with
 * only the name to judge, which is the whole failure. The counts are the reader's first check --
 * and they are deliberately NOT the decision, because a count cannot tell a finished feature from
 * one whose capability never ran once (F-130 and F-134 were countably identical). Reading the
 * acceptance sentence is what separates them, so the dialog leads with it.
 *
 * DRAGGING TO DONE IS FINE, AND ASKS. Moving a card to Done prospectively is ordinary practice --
 * somebody finishes, says so, and comes back later if they were wrong. Refusing the drag would not
 * produce a better assessment, it would produce none. So the drop lands AND opens the acceptance
 * dialog, which is an invitation rather than a toll gate: cancel it and the Feature is still Done,
 * carrying a visible "no acceptance recorded yet" on its card until somebody writes one.
 */
class PictViewFeatureBoard extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this._drag = null;
		// Cards picked for one decision about a set. Kept as IDFeature, which is what the bulk
		// endpoint takes.
		this._selected = new Set();
		this._lastSelected = 0;
	}

	_board() { return this.pict.providers.FeatureBoardAPI; }
	_router() { return this.pict.providers['Pict-Router']; }

	show()
	{
		let tmpBoard = this._board();
		if (!tmpBoard) { return Promise.resolve(); }
		return tmpBoard.list().then(() => { this._decorate(); this.render(); });
	}
	refresh() { return this.show(); }

	/**
	 * Turn the raw card into what the template renders. The counts become one readable phrase
	 * ("6 of 8 done") plus a progress width, and the decision buttons become a slot that is empty
	 * unless this card is actually in Review and this reader can actually decide.
	 */
	_decorate()
	{
		let tmpStore = (this.pict.AppData && this.pict.AppData.FeatureBoard) || null;
		if (!tmpStore) { return; }
		let tmpCanAccept = !!tmpStore.CanAccept;

		(tmpStore.Lanes || []).forEach((pLane) =>
		{
			(pLane.Cards || []).forEach((pCard) =>
			{
				let tmpConnected = Number(pCard.WorkItemsConnected) || 0;
				let tmpDone = Number(pCard.WorkItemsDone) || 0;
				pCard.Designator = 'F-' + String(pCard.FeatureNumber || pCard.IDFeature);
				pCard.WorkLabel = tmpConnected
					? (tmpDone + ' of ' + tmpConnected + ' work item' + (tmpConnected === 1 ? '' : 's') + ' done')
					// Said plainly rather than shown as "0 of 0": a feature with no work items is a
					// discipline problem (file the item before the code), and no board rule can find
					// it. Naming it on the card is the only place it becomes visible at all.
					: 'no work items connected';
				pCard.WorkPercent = tmpConnected ? Math.round((tmpDone / tmpConnected) * 100) : 0;
				pCard.WorkBarStyle = 'width:' + pCard.WorkPercent + '%';
				pCard.BuildLabel = (Number(pCard.Commits) || 0)
					? ((pCard.Commits) + ' commit' + (Number(pCard.Commits) === 1 ? '' : 's')
						+ ', ' + (Number(pCard.AsBuiltsDeployed) || 0) + ' deployed')
					: '';
				pCard.LandedLabel = pCard.LastLandedDate ? ('landed ' + String(pCard.LastLandedDate).slice(0, 10)) : '';
				pCard.AcceptedLabel = pCard.AcceptedDate ? ('accepted ' + String(pCard.AcceptedDate).slice(0, 10)) : '';
				// The two signals the acceptance work item asked to be ON the card, not buried a click
				// deep: a connected item still bouncing off review, and this feature's own rebuild
				// history. Neither is the decision -- reading the sentence still is -- but both are
				// exactly the kind of thing that separates a genuinely finished feature from one that
				// only looks it.
				pCard.ReworkLabel = pCard.StillFailingReview ? 'a work item is still failing review' : '';
				pCard.RebuildLabel = (Number(pCard.PriorAcceptances) || 0)
					? ('accepted ' + pCard.PriorAcceptances + 'x before') : '';
				// A Done card nobody has written an assessment on. Surfaced, never prevented: the
				// move is legitimate, the missing note is the thing worth noticing.
				pCard.AwaitingSlot = pCard.AwaitingAcceptance ? [{ IDFeature: pCard.IDFeature }] : [];
				// The one-line preview of the sentence being judged. The dialog shows all of it.
				pCard.SentencePreview = String(pCard.AcceptanceSentence || '').slice(0, 150);
				// Offered in Review (the normal moment) and on a Done card still missing its
				// assessment (the after-the-fact moment), so a note can always be written.
				pCard.DecideSlot = ((pCard.Status === 'Review' || pCard.AwaitingAcceptance) && tmpCanAccept)
					? [{ IDFeature: pCard.IDFeature, BackLabel: (pCard.Status === 'Done') ? 'Reopen' : 'Send back' }] : [];
				// A card only offers selection if it could actually be decided, so the count on the
				// bar is never larger than what a decision would move.
				pCard.PickSlot = pCard.DecideSlot.length
					? [{ IDFeature: pCard.IDFeature, CheckedAttr: this._selected.has(pCard.IDFeature) ? 'checked' : '' }] : [];
				pCard.SelectedClass = this._selected.has(pCard.IDFeature) ? ' ps-fb-card-selected' : '';
			});
		});

		// The picking chrome. Only ever offered to somebody who can actually decide, and only when
		// there is something decidable to pick.
		let tmpPickable = this._pickableOrder().length;
		tmpStore.PickAllSlot = (tmpCanAccept && tmpPickable && this._selected.size < tmpPickable)
			? [{ Pickable: tmpPickable }] : [];
		tmpStore.BulkSlot = (tmpCanAccept && this._selected.size)
			? [{ Count: this._selected.size, Plural: (this._selected.size === 1) ? '' : 's' }] : [];
	}

	// The still-open work items ticked in the confirm list.
	_readTicks()
	{
		if (typeof document === 'undefined') { return []; }
		return Array.prototype.slice.call(document.querySelectorAll('.ps-fb-close-item'))
			.filter((pBox) => pBox.checked).map((pBox) => Number(pBox.value) || 0).filter((pID) => pID);
	}

	/**
	 * Mount the rich markdown editor into the open dialog.
	 *
	 * The note is markdown, so it gets the editor the rest of the app writes markdown in rather than
	 * a bare textarea: the same CodeMirror, the same live preview, and the same paste-an-image
	 * handling. Its own instance, because the entity form's is live at the same time.
	 */
	_mountNoteEditor()
	{
		let tmpEditor = this.pict.views['Plansheet-AcceptanceMarkdownEditor'];
		if (!tmpEditor)
		{
			// Not in this build. A plain textarea still collects the note rather than the field
			// silently not existing.
			this.pict.ContentAssignment.assignContent('#Acceptance-MarkdownHost',
				'<textarea id="ps-fb-note" class="ps-fb-dlg-note-fallback"></textarea>');
			let tmpElement = (typeof document !== 'undefined') ? document.getElementById('ps-fb-note') : null;
			if (tmpElement) { tmpElement.oninput = () => { this._note = tmpElement.value; }; }
			return;
		}
		this.pict.AppData.AcceptanceMarkdownEditor = this.pict.AppData.AcceptanceMarkdownEditor || {};
		this.pict.AppData.AcceptanceMarkdownEditor.Segments = [{ Content: '' }];
		try { if (tmpEditor.initialRenderComplete) { tmpEditor.destroy(); tmpEditor.initialRenderComplete = false; } }
		catch (pError) { /* ignore -- a re-mount over a dead instance */ }
		tmpEditor.render();
		tmpEditor.marshalToView();
		if (typeof tmpEditor.setPreviewMode === 'function') { tmpEditor.setPreviewMode('bottom'); }
	}

	// The dialog's DOM is thrown away when it closes, so the editor has to go with it or it keeps
	// CodeMirror instances and timers alive against detached nodes.
	_teardownNoteEditor()
	{
		let tmpEditor = this.pict.views['Plansheet-AcceptanceMarkdownEditor'];
		if (!tmpEditor || !tmpEditor.initialRenderComplete) { return; }
		try { tmpEditor.destroy(); tmpEditor.initialRenderComplete = false; }
		catch (pError) { /* ignore -- teardown is best-effort */ }
	}

	// The editor calls this on every change; the note is kept here so it is in hand before the
	// dialog dismisses (see decide()).
	onMarkdownChanged()
	{
		let tmpEditor = this.pict.views['Plansheet-AcceptanceMarkdownEditor'];
		if (!tmpEditor || typeof tmpEditor.getAllContent !== 'function') { return; }
		this._note = tmpEditor.getAllContent('\n\n');
	}

	// An image pasted into the note belongs to the feature being judged, so it lands in that
	// feature's own gallery rather than as a base64 blob inside the note.
	markdownOwnerContext() { return this._noteOwner || { OwnerType: '', IDOwner: 0 }; }

	/**
	 * Turn this dialog's primary button into its own confirm, in place.
	 *
	 * The modal dismisses on the FIRST click of any footer button, so the second click has to be
	 * taken before that listener runs. A capture listener on the dialog fires ahead of the button's
	 * own, which is what lets the button relabel itself here rather than a second modal appearing on
	 * top of the first -- the note, the evidence and the ticks all stay on screen while you confirm.
	 *
	 * Only arms when the note is empty. Prose is its own deliberateness; making somebody who just
	 * wrote a paragraph click twice proves nothing.
	 */
	_armInPlaceConfirm(pHostID, pLabel)
	{
		if (typeof document === 'undefined') { return; }
		let tmpHost = document.getElementById(pHostID);
		let tmpDialog = (tmpHost && tmpHost.closest) ? tmpHost.closest('.pict-modal-dialog') : null;
		if (!tmpDialog) { return; }
		let tmpButton = tmpDialog.querySelector('[data-hash="ok"]');
		if (!tmpButton) { return; }

		let tmpArmed = false;
		tmpDialog.addEventListener('click', (pEvent) =>
		{
			if (!pEvent.target || !pEvent.target.closest) { return; }
			if (pEvent.target.closest('[data-hash="ok"]') !== tmpButton) { return; }
			if (tmpArmed) { return; }
			// Typed something since arming (or before ever arming): let it straight through.
			if (String(this._note || '').trim()) { return; }
			tmpArmed = true;
			tmpButton.textContent = pLabel;
			tmpButton.classList.add('ps-fb-confirming');
			pEvent.stopImmediatePropagation();
			pEvent.preventDefault();
		}, true);
	}

	/**
	 * Move one card to where the decision just put it, without re-reading the board.
	 *
	 * Refetching after every decision is what makes a queue feel like wading: the whole surface
	 * blanks and repaints to change one card. Nothing else moved, so nothing else needs redrawing.
	 */
	_applyDecision(pIDFeature, pOutcome, pResult)
	{
		let tmpStore = (this.pict.AppData && this.pict.AppData.FeatureBoard) || null;
		if (!tmpStore || !Array.isArray(tmpStore.Lanes)) { return; }
		let tmpTargetKey = (pOutcome === 'Accept') ? 'Done' : 'InProgress';

		let tmpCard = null;
		tmpStore.Lanes.forEach((pLane) =>
		{
			let tmpIndex = (pLane.Cards || []).findIndex((pRow) => Number(pRow.IDFeature) === Number(pIDFeature));
			if (tmpIndex >= 0) { tmpCard = pLane.Cards.splice(tmpIndex, 1)[0]; }
		});
		if (!tmpCard) { return this.refresh(); }

		let tmpClosed = ((pResult && pResult.ClosedWorkItems) || []).length;
		tmpCard.Status = tmpTargetKey;
		tmpCard.WorkItemsDone = Math.min(Number(tmpCard.WorkItemsConnected) || 0, (Number(tmpCard.WorkItemsDone) || 0) + tmpClosed);
		tmpCard.WorkItemsOpen = Math.max(0, (Number(tmpCard.WorkItemsConnected) || 0) - tmpCard.WorkItemsDone);
		if (pOutcome === 'Accept')
		{
			tmpCard.AcceptedDate = (pResult && pResult.DecisionDate) || (new Date()).toISOString();
			tmpCard.AwaitingAcceptance = false;
		}
		else { tmpCard.AwaitingAcceptance = false; }

		let tmpLane = tmpStore.Lanes.filter((pLane) => pLane.Key === tmpTargetKey)[0];
		if (tmpLane) { tmpLane.Cards.unshift(tmpCard); }
		tmpStore.Lanes.forEach((pLane) => { pLane.Count = (pLane.Cards || []).length; });

		this._decorate();
		this.render();
	}

	// ---- picking a set --------------------------------------------------------------

	// Shift-click extends from the last pick, the usual list gesture, so a run of a hundred is two
	// clicks rather than a hundred.
	togglePick(pEvent, pIDFeature)
	{
		if (pEvent && typeof pEvent.stopPropagation === 'function') { pEvent.stopPropagation(); }
		let tmpID = Number(pIDFeature) || 0;
		if (!tmpID) { return; }

		if (pEvent && pEvent.shiftKey && this._lastSelected)
		{
			let tmpOrder = this._pickableOrder();
			let tmpFrom = tmpOrder.indexOf(this._lastSelected);
			let tmpTo = tmpOrder.indexOf(tmpID);
			if (tmpFrom >= 0 && tmpTo >= 0)
			{
				for (let i = Math.min(tmpFrom, tmpTo); i <= Math.max(tmpFrom, tmpTo); i++) { this._selected.add(tmpOrder[i]); }
			}
			else { this._selected.add(tmpID); }
		}
		else if (this._selected.has(tmpID)) { this._selected.delete(tmpID); }
		else { this._selected.add(tmpID); }

		this._lastSelected = tmpID;
		this._decorate();
		this.render();
	}

	// Every decidable card, in the order they are read, so a shift-range means what it looks like.
	_pickableOrder()
	{
		let tmpStore = (this.pict.AppData && this.pict.AppData.FeatureBoard) || {};
		let tmpOrder = [];
		(tmpStore.Lanes || []).forEach((pLane) => (pLane.Cards || []).forEach((pCard) =>
		{
			if (pCard.Status === 'Review' || pCard.AwaitingAcceptance) { tmpOrder.push(pCard.IDFeature); }
		}));
		return tmpOrder;
	}

	// Pick every card that could be decided. This is the backfill button: a hundred features that
	// were finished before there was anywhere to record an acceptance are one judgement, not a
	// hundred, and clicking them individually would be the ceremony rather than the substance.
	pickAll()
	{
		this._pickableOrder().forEach((pID) => this._selected.add(pID));
		this._decorate();
		this.render();
	}

	clearPicks()
	{
		this._selected.clear();
		this._lastSelected = 0;
		this._decorate();
		this.render();
	}

	/**
	 * One decision about every picked feature.
	 *
	 * Deliberately the same dialog shape as the single decision -- what is being judged, then the
	 * note, then confirm -- because it is the same act. What differs is that the thing being judged
	 * is a SET, so the dialog names every feature in it rather than showing one promise. A person
	 * has to be able to see exactly what they are about to decide.
	 */
	decideMany(pOutcome)
	{
		let tmpBoard = this._board();
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpBoard || !tmpModal || typeof tmpModal.show !== 'function') { return; }
		let tmpIDs = Array.from(this._selected);
		if (!tmpIDs.length) { return; }
		let tmpAccepting = (pOutcome === 'Accept');

		let tmpStore = (this.pict.AppData && this.pict.AppData.FeatureBoard) || {};
		let tmpPicked = [];
		(tmpStore.Lanes || []).forEach((pLane) => (pLane.Cards || []).forEach((pCard) =>
		{
			if (this._selected.has(pCard.IDFeature)) { tmpPicked.push(pCard); }
		}));
		let tmpOpenWork = tmpPicked.reduce((pSum, pCard) => pSum + (Number(pCard.WorkItemsOpen) || 0), 0);

		let fEsc = (pText) => String(pText == null ? '' : pText)
			.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

		let tmpContent =
			'<div class="ps-fb-dlg-evidence">One decision about <b>' + tmpPicked.length + '</b> feature'
				+ (tmpPicked.length === 1 ? '' : 's') + '. It is recorded on each of them as one judgement about '
				+ tmpPicked.length + ', not as ' + tmpPicked.length + ' separate readings.</div>'
			+ '<div class="ps-fb-dlg-items">' + tmpPicked.map((pCard) =>
				'<div class="ps-fb-dlg-item"><span class="n">F-' + fEsc(pCard.FeatureNumber) + '</span>'
				+ '<span class="s">' + fEsc(pCard.Status) + '</span>'
				+ '<span class="t">' + fEsc(pCard.Name) + '</span></div>').join('') + '</div>'
			+ (tmpAccepting && tmpOpenWork
				? ('<label class="ps-fb-dlg-item ps-fb-dlg-closeall"><input type="checkbox" id="ps-fb-close-all" checked>'
					+ '<span class="t">Also mark the <b>' + tmpOpenWork + '</b> work item'
					+ (tmpOpenWork === 1 ? '' : 's') + ' still open under them Done</span></label>')
				: '')
			+ '<div class="ps-fb-dlg-label">'
				+ (tmpAccepting ? 'Why are these promises kept?' : 'What is missing?')
				+ '<span class="ps-fb-dlg-optional">optional, recorded on every one</span></div>'
			+ '<div id="Acceptance-MarkdownHost" class="ps-fb-dlg-note"></div>';

		this._note = '';
		this._noteOwner = { OwnerType: '', IDOwner: 0 };
		let tmpCloseAll = false;

		return tmpModal.show(
			{
				title: (tmpAccepting ? 'Accept ' : 'Send back ') + tmpPicked.length + ' feature' + (tmpPicked.length === 1 ? '' : 's'),
				content: tmpContent,
				width: 720,
				onOpen: () =>
				{
					this._mountNoteEditor();
					this._armInPlaceConfirm('Acceptance-MarkdownHost',
						(tmpAccepting ? 'Accept all anyway' : 'Send all back anyway'));
				},
				onClose: () =>
				{
					let tmpBox = (typeof document !== 'undefined') ? document.getElementById('ps-fb-close-all') : null;
					tmpCloseAll = !!(tmpBox && tmpBox.checked);
				},
				buttons: [{ Hash: 'cancel', Label: 'Cancel' },
					{ Hash: 'ok', Label: (tmpAccepting ? 'Accept all' : 'Send all back'), Style: 'primary' }]
			}).then((pChoice) =>
			{
				let tmpNote = String(this._note || '').trim();
				this._teardownNoteEditor();
				if (pChoice !== 'ok') { return null; }

				return tmpBoard.decideMany(tmpIDs, pOutcome, tmpNote, tmpCloseAll).then((pResult) =>
				{
					let tmpDecided = (pResult && pResult.Decided) || [];
					let tmpSkipped = (pResult && pResult.Skipped) || [];
					this.clearPicks();
					// A set decision moves many cards at once, so this one does re-read the board.
					// Patching N cards by hand would be a second implementation of the server's rules
					// and would drift from them.
					this.refresh();
					this._toast(tmpDecided.length + (tmpAccepting ? ' accepted' : ' sent back')
						+ ((pResult && pResult.ClosedWorkItems) ? (', ' + pResult.ClosedWorkItems + ' work items closed') : '')
						+ (tmpSkipped.length ? (', ' + tmpSkipped.length + ' skipped') : '') + '.',
						tmpSkipped.length ? 'info' : 'success');
					return pResult;
				});
			}).catch((pError) =>
			{
				this._toast('Could not record the decisions: ' + ((pError && pError.message) || pError), 'error');
			});
	}

	_toast(pMessage, pType)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.toast === 'function')
		{
			tmpModal.toast(pMessage, { type: pType || 'info', duration: (pType === 'error') ? 6000 : 2500 });
		}
	}

	openFeature(pID)
	{
		let tmpRouter = this._router();
		if (tmpRouter && pID) { tmpRouter.navigate('/feature/' + pID); }
	}

	/**
	 * The decision. Reads the evidence first, then shows it: the acceptance sentence at the top,
	 * because that is the thing being judged, then the counts and the work items behind them, then
	 * an empty markdown box the reader has to fill in either way.
	 *
	 * pOutcome is 'Accept' or 'SendBack'. Both go through the same dialog because they are the same
	 * decision pointed in two directions, and both require a note -- the server refuses an empty
	 * one, and so does this, so the reader is told before the round trip rather than after.
	 */
	decide(pEvent, pIDFeature, pOutcome)
	{
		if (pEvent && typeof pEvent.stopPropagation === 'function') { pEvent.stopPropagation(); }
		let tmpBoard = this._board();
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpBoard || !tmpModal || typeof tmpModal.show !== 'function') { return; }
		let tmpAccepting = (pOutcome === 'Accept');

		tmpBoard.acceptance(pIDFeature).then((pEvidence) =>
		{
			let tmpFeature = (pEvidence && pEvidence.Feature) || {};
			let tmpItems = (pEvidence && pEvidence.WorkItems) || [];
			let tmpDone = tmpItems.filter((pItem) => pItem.Status === 'Done');
			// Review first, because those are the ones already ticked and the reader should see them
			// before the ones they have to opt into.
			let tmpOpen = tmpItems.filter((pItem) => pItem.Status !== 'Done')
				.sort((pA, pB) => ((pB.Status === 'Review') ? 1 : 0) - ((pA.Status === 'Review') ? 1 : 0));
			let tmpHistory = (pEvidence && pEvidence.History) || [];
			let fEsc = (pText) => String(pText == null ? '' : pText)
				.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

			let tmpSentence = String(tmpFeature.AcceptanceSentence || '').trim();
			let tmpContent =
				'<div class="ps-fb-dlg-sentence"><div class="ps-fb-dlg-label">What this feature promised</div>'
					+ '<div class="ps-fb-dlg-quote">' + (tmpSentence ? fEsc(tmpSentence)
						: '<i>Nothing is written down. Decide from the work below, then write the promise on the feature so the next reader has it.</i>') + '</div></div>'
				+ '<div class="ps-fb-dlg-evidence">'
					+ '<b>' + (Number(tmpFeature.WorkItemsDone) || 0) + '</b> of <b>' + (Number(tmpFeature.WorkItemsConnected) || 0) + '</b> work items done'
					+ ', <b>' + (Number(tmpFeature.Commits) || 0) + '</b> commits'
					+ ', <b>' + (Number(tmpFeature.AsBuiltsDeployed) || 0) + '</b> deployed'
					+ ', <b>' + (Number(tmpFeature.AsBuiltsValidated) || 0) + '</b> validated'
					+ (tmpFeature.LastLandedDate ? (', last landed ' + fEsc(String(tmpFeature.LastLandedDate).slice(0, 10))) : '')
				+ '</div>'
				+ (tmpDone.length
					? ('<div class="ps-fb-dlg-items">' + tmpDone.map((pItem) =>
						'<div class="ps-fb-dlg-item"><span class="n">WI-' + fEsc(pItem.WorkItemNumber) + '</span>'
						+ '<span class="s">' + fEsc(pItem.Status) + '</span>'
						+ '<span class="t">' + fEsc(pItem.Title) + '</span></div>').join('') + '</div>')
					: '')
				// The tickets still open under this feature, as a confirm list. Anything in Review is
				// ticked to begin with, because that is the lane that fills up and the one a feature
				// being accepted almost always settles; everything else is offered unticked so it is
				// added on purpose rather than swept up. Only ever shown when accepting: sending a
				// feature back says the capability is not there, which says nothing about anybody's
				// individual ticket.
				+ ((tmpAccepting && tmpOpen.length)
					? ('<div class="ps-fb-dlg-label">Also mark these Done?</div>'
						+ '<div class="ps-fb-dlg-items ps-fb-dlg-close">' + tmpOpen.map((pItem) =>
							'<label class="ps-fb-dlg-item"><input type="checkbox" class="ps-fb-close-item" value="'
							+ fEsc(pItem.IDWorkItem) + '"' + ((pItem.Status === 'Review') ? ' checked' : '') + '>'
							+ '<span class="n">WI-' + fEsc(pItem.WorkItemNumber) + '</span>'
							+ '<span class="s">' + fEsc(pItem.Status) + '</span>'
							+ '<span class="t">' + fEsc(pItem.Title) + '</span></label>').join('') + '</div>')
					: '')
				+ (tmpHistory.length
					? ('<div class="ps-fb-dlg-history"><div class="ps-fb-dlg-label">Decided before</div>'
						+ tmpHistory.map((pRow) =>
							'<div class="ps-fb-dlg-item"><span class="s">' + fEsc(pRow.Outcome) + '</span>'
							+ '<span class="n">' + fEsc(String(pRow.DecisionDate).slice(0, 10)) + '</span>'
							+ '<span class="t">' + fEsc(String(pRow.AcceptanceNote || '').slice(0, 160)) + '</span></div>').join('')
						+ '</div>')
					: '')
				+ '<div class="ps-fb-dlg-label">'
					+ (tmpAccepting ? 'Why is this promise kept?' : 'What is missing?')
					+ '<span class="ps-fb-dlg-optional">optional</span></div>'
				+ '<div id="Acceptance-MarkdownHost" class="ps-fb-dlg-note"></div>';

			// The editor writes back on every keystroke (onMarkdownChanged below), so the note is in
			// hand before the dialog dismisses and there is nothing to read off dying DOM.
			this._note = '';
			this._noteOwner = { OwnerType: 'Feature', IDOwner: pIDFeature };

			return tmpModal.show(
				{
					title: (tmpAccepting ? 'Accept ' : 'Send back ') + 'F-' + (tmpFeature.FeatureNumber || pIDFeature)
						+ ': ' + (tmpFeature.Name || ''),
					content: tmpContent,
					width: 720,
					onOpen: () =>
					{
						this._mountNoteEditor();
						this._armInPlaceConfirm('Acceptance-MarkdownHost',
							(tmpAccepting ? 'Accept anyway' : 'Send back anyway'));
					},
					// onClose runs synchronously BEFORE the promise resolves and 220ms before the
					// dialog is removed, so the ticks are read while the DOM is certainly still there
					// rather than inside an animation window that happens to be long enough.
					onClose: () => { this._ticked = this._readTicks(); },
					buttons: [{ Hash: 'cancel', Label: 'Cancel' },
						{ Hash: 'ok', Label: (tmpAccepting ? 'Accept feature' : 'Send back'), Style: 'primary' }]
				}).then((pChoice) =>
				{
					let tmpNote = String(this._note || '').trim();
					let tmpClose = this._ticked || [];
					this._teardownNoteEditor();
					if (pChoice !== 'ok') { return null; }

					// The empty-note second click already happened, in this window, on this button
					// (_armInPlaceConfirm). By the time the dialog resolves 'ok' the decision is
					// established either by prose or by two clicks, so there is nothing left to ask.
					return tmpBoard.decide(pIDFeature, pOutcome, tmpNote, tmpClose).then((pResult) =>
					{
						let tmpClosed = (pResult && pResult.ClosedWorkItems) || [];
						// Patch the one card rather than re-fetching every feature on the board. A
						// whole reload per decision is what makes working through a queue feel like
						// wading, and nothing else on the board changed.
						this._applyDecision(pIDFeature, pOutcome, pResult);
						this._toast(tmpAccepting
							? ('Accepted. The feature is Done'
								+ (tmpClosed.length ? (', and ' + tmpClosed.length + ' work item' + (tmpClosed.length === 1 ? '' : 's') + ' with it') : '') + '.')
							: 'Sent back to In Progress.', 'success');
						return pResult;
					});
				});
		}).catch((pError) =>
		{
			this._toast('Could not record the decision: ' + ((pError && pError.message) || pError), 'error');
		});
	}

	dragStart(pEvent, pID)
	{
		this._drag = { ID: pID };
		if (pEvent && pEvent.dataTransfer)
		{
			pEvent.dataTransfer.effectAllowed = 'move';
			try { pEvent.dataTransfer.setData('text/plain', String(pID)); } catch (pError) { /* ignore */ }
		}
	}

	allowDrop(pEvent)
	{
		if (pEvent && typeof pEvent.preventDefault === 'function') { pEvent.preventDefault(); }
		if (pEvent && pEvent.dataTransfer) { pEvent.dataTransfer.dropEffect = 'move'; }
	}

	drop(pEvent, pLaneKey)
	{
		if (pEvent && typeof pEvent.preventDefault === 'function') { pEvent.preventDefault(); }
		let tmpDrag = this._drag;
		this._drag = null;
		let tmpBoard = this._board();
		if (!tmpDrag || !tmpBoard) { return; }

		tmpBoard.moveCard(tmpDrag.ID, pLaneKey, tmpBoard.nextSortOrder(pLaneKey))
			.then(() => this.show())
			// Dropping on Done is the moment somebody has decided something, so it is the moment to
			// ask what. The move has already landed by here: the dialog collects the reason, it does
			// not hold the card hostage for one, and cancelling leaves a Done Feature saying it has
			// no acceptance recorded.
			.then(() => { if (pLaneKey === 'Done') { this.decide(null, tmpDrag.ID, 'Accept'); } })
			.catch((pError) => { this._toast('Move failed: ' + ((pError && pError.message) || pError), 'error'); });
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}
}

module.exports = PictViewFeatureBoard;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-FeatureBoard',
	DefaultRenderable: 'FeatureBoard-Display',
	AutoRender: false,
	CSS: /*css*/`
		.ps-fb { height: 100%; display: flex; flex-direction: column; gap: 14px; }
		.ps-fb-head { display: flex; align-items: baseline; gap: 14px; flex-wrap: wrap; }
		.ps-fb-head h2 { font-size: 22px; font-weight: 600; color: var(--theme-color-text-primary, #2c3140); margin: 0; }
		.ps-fb-blurb { font-size: 13px; color: var(--theme-color-text-secondary, #5b6376); }
		/* One scroll region for the whole board; lanes never scroll on their own. */
		.ps-fb-lanes { display: flex; gap: 12px; overflow: auto; align-items: flex-start; flex: 1; min-height: 0; padding-bottom: 8px; }
		.ps-fb-lane { flex: 0 0 268px; width: 268px; background: var(--theme-color-background-secondary, #f1f3f7); border-radius: 10px; padding: 10px; display: flex; flex-direction: column; }
		.ps-fb-lane-head { display: flex; align-items: center; justify-content: space-between; font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-secondary, #5b6376); padding: 4px 6px 10px; }
		.ps-fb-lane-count { background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-secondary, #5b6376); border-radius: 999px; padding: 1px 8px; font-size: 12px; }
		.ps-fb-lane-cards { display: flex; flex-direction: column; gap: 8px; min-height: 40px; }
		.ps-fb-card { background: var(--theme-color-background-panel, #fff); border-radius: 8px; padding: 10px 12px; border-left: 3px solid var(--theme-color-brand-primary, #2880a6); cursor: pointer; box-shadow: 0 1px 2px rgba(0,0,0,0.06); }
		.ps-fb-card:hover { box-shadow: 0 2px 6px rgba(0,0,0,0.12); }
		.ps-fb-card-top { display: flex; align-items: baseline; gap: 6px; margin-bottom: 4px; }
		.ps-fb-card-num { font-size: 11px; color: var(--theme-color-text-muted, #97a1ab); font-weight: 700; }
		.ps-fb-card-title { font-size: 14px; color: var(--theme-color-text-primary, #2c3140); line-height: 1.25; }
		.ps-fb-card-sentence { font-size: 12px; color: var(--theme-color-text-secondary, #5b6376); line-height: 1.35; margin-bottom: 7px; }
		.ps-fb-card-sentence:empty { display: none; }
		.ps-fb-work { font-size: 11px; color: var(--theme-color-text-secondary, #5b6376); margin-bottom: 3px; }
		.ps-fb-bar { height: 4px; border-radius: 999px; background: var(--theme-color-background-tertiary, #e3e8f0); overflow: hidden; margin-bottom: 6px; }
		.ps-fb-bar span { display: block; height: 100%; background: var(--theme-color-status-success, #3fae6b); }
		.ps-fb-meta { display: flex; gap: 5px; flex-wrap: wrap; }
		.ps-fb-chip { font-size: 11px; padding: 1px 7px; border-radius: 999px; background: var(--theme-color-background-secondary, #eef1f6); color: var(--theme-color-text-secondary, #5b6376); }
		.ps-fb-chip:empty { display: none; }
		.ps-fb-chip-warn:not(:empty) { background: var(--theme-color-status-warning-background, #fbe9dd); color: var(--theme-color-status-warning, #d99366); font-weight: 600; }
		.ps-fb-decide { display: flex; gap: 6px; margin-top: 8px; }
		.ps-fb-decide button { font: inherit; font-size: 12px; border-radius: 6px; padding: 4px 10px; cursor: pointer; border: 1px solid var(--theme-color-border-default, #d8dde6); background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #2c3140); }
		.ps-fb-accept { border-color: var(--theme-color-status-success, #3fae6b) !important; color: var(--theme-color-status-success, #3fae6b) !important; font-weight: 600; }
		.ps-fb-back { border-color: var(--theme-color-status-warning, #d99366) !important; color: var(--theme-color-status-warning, #d99366) !important; }
		.ps-fb-dlg-close .ps-fb-dlg-item { cursor: pointer; gap: 8px; padding: 4px 0; }
		.ps-fb-dlg-close .ps-fb-dlg-item:hover { background: var(--theme-color-background-secondary, #eef1f6); }
		.ps-fb-dlg-close input { margin: 0; flex: 0 0 auto; }
		.pict-modal-btn.ps-fb-confirming { background: var(--theme-color-status-warning, #d99366) !important; border-color: var(--theme-color-status-warning, #d99366) !important; }
		.ps-fb-card-check { margin: 0 6px 0 0; flex: 0 0 auto; cursor: pointer; }
		.ps-fb-card-selected { outline: 2px solid var(--theme-color-brand-primary, #2880a6); outline-offset: -1px; }
		.ps-fb-pickall { font: inherit; font-size: 12px; border: 1px solid var(--theme-color-border-default, #d8dde6); background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-secondary, #5b6376); border-radius: 6px; padding: 4px 10px; cursor: pointer; margin-left: auto; }
		.ps-fb-pickall:hover { border-color: var(--theme-color-brand-primary, #2880a6); color: var(--theme-color-brand-primary, #2880a6); }
		.ps-fb-bulkbar { position: sticky; bottom: 0; display: flex; align-items: center; gap: 10px; padding: 10px 14px; border-radius: 10px; background: var(--theme-color-background-panel, #fff); border: 1px solid var(--theme-color-border-default, #d8dde6); box-shadow: 0 -2px 10px rgba(0,0,0,0.10); }
		.ps-fb-bulkcount { font-size: 13px; font-weight: 600; color: var(--theme-color-text-primary, #2c3140); }
		.ps-fb-bulkbar button { font: inherit; font-size: 12px; border-radius: 6px; padding: 5px 12px; cursor: pointer; border: 1px solid var(--theme-color-border-default, #d8dde6); background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #2c3140); }
		.ps-fb-bulkclear { margin-left: auto; color: var(--theme-color-text-secondary, #5b6376) !important; }
		.ps-fb-dlg-closeall { cursor: pointer; gap: 8px; margin-top: 8px; border-bottom: none !important; }
		.ps-fb-awaiting { margin-top: 7px; font-size: 11px; color: var(--theme-color-status-warning, #d99366); }
		.ps-fb-dlg-label { font-size: 12px; font-weight: 600; color: var(--theme-color-text-secondary, #5b6376); display: block; margin: 10px 0 4px; }
		.ps-fb-dlg-quote { font-size: 14px; line-height: 1.4; color: var(--theme-color-text-primary, #2c3140); border-left: 3px solid var(--theme-color-brand-primary, #2880a6); padding: 4px 0 4px 10px; white-space: pre-wrap; }
		.ps-fb-dlg-evidence { font-size: 12px; color: var(--theme-color-text-secondary, #5b6376); margin: 10px 0 0; }
		.ps-fb-dlg-items, .ps-fb-dlg-history { max-height: 150px; overflow: auto; margin-top: 6px; }
		.ps-fb-dlg-item { display: flex; gap: 7px; align-items: baseline; font-size: 12px; padding: 2px 0; border-bottom: 1px solid var(--theme-color-border-light, #f0f3f6); }
		.ps-fb-dlg-item .n { color: var(--theme-color-text-muted, #97a1ab); font-weight: 700; flex: 0 0 auto; }
		.ps-fb-dlg-item .s { color: var(--theme-color-text-secondary, #5b6376); flex: 0 0 auto; }
		.ps-fb-dlg-item .t { color: var(--theme-color-text-primary, #2c3140); }
		.ps-fb-dlg-optional { font-weight: 400; font-size: 11px; color: var(--theme-color-text-muted, #97a1ab); margin-left: 6px; text-transform: none; letter-spacing: 0; }
		.ps-fb-dlg-note { min-height: 220px; }
		.ps-fb-dlg-note-fallback { width: 100%; box-sizing: border-box; min-height: 110px; resize: vertical; padding: 7px 9px; font: inherit; font-size: 13px; border-radius: 6px; border: 1px solid var(--theme-color-border-default, #d8dde6); background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #2c3140); }
	`,
	Templates:
	[
		{
			Hash: 'FeatureBoard-Display',
			Template: /*html*/`
<div class="ps-page ps-fb">
	<div class="ps-fb-head">
		<h2>Features</h2>
		<span class="ps-fb-blurb">A feature is finished when its promise is kept. Move one to Review, read what it promised, and say whether it was.</span>
		{~TS:FeatureBoard-PickAll:AppData.FeatureBoard.PickAllSlot~}
	</div>
	<div class="ps-fb-lanes">{~TS:FeatureBoard-Lane:AppData.FeatureBoard.Lanes~}</div>
	{~TS:FeatureBoard-BulkBar:AppData.FeatureBoard.BulkSlot~}
</div>`
		},
		{
			Hash: 'FeatureBoard-Lane',
			Template: /*html*/`
<div class="ps-fb-lane" ondragover="_Pict.views['Plansheet-FeatureBoard'].allowDrop(event)" ondrop="_Pict.views['Plansheet-FeatureBoard'].drop(event,'{~D:Record.Key~}')">
	<div class="ps-fb-lane-head"><span>{~D:Record.Label~}</span><span class="ps-fb-lane-count">{~D:Record.Count~}</span></div>
	<div class="ps-fb-lane-cards">{~TS:FeatureBoard-Card:Record.Cards~}</div>
</div>`
		},
		{
			Hash: 'FeatureBoard-Card',
			Template: /*html*/`
<div class="ps-fb-card{~D:Record.SelectedClass~}" draggable="true" ondragstart="_Pict.views['Plansheet-FeatureBoard'].dragStart(event,{~D:Record.IDFeature~})" onclick="_Pict.views['Plansheet-FeatureBoard'].openFeature({~D:Record.IDFeature~})">
	<div class="ps-fb-card-top">{~TS:FeatureBoard-Pick:Record.PickSlot~}<span class="ps-fb-card-num">{~D:Record.Designator~}</span><span class="ps-fb-card-title">{~D:Record.Name~}</span></div>
	<div class="ps-fb-card-sentence">{~D:Record.SentencePreview~}</div>
	<div class="ps-fb-work">{~D:Record.WorkLabel~}</div>
	<div class="ps-fb-bar"><span style="{~D:Record.WorkBarStyle~}"></span></div>
	<div class="ps-fb-meta">
		<span class="ps-fb-chip">{~D:Record.BuildLabel~}</span>
		<span class="ps-fb-chip">{~D:Record.LandedLabel~}</span>
		<span class="ps-fb-chip">{~D:Record.AcceptedLabel~}</span>
		<span class="ps-fb-chip">{~D:Record.RebuildLabel~}</span>
		<span class="ps-fb-chip ps-fb-chip-warn">{~D:Record.ReworkLabel~}</span>
	</div>
	{~TS:FeatureBoard-Awaiting:Record.AwaitingSlot~}
	{~TS:FeatureBoard-Decide:Record.DecideSlot~}
</div>`
		},
		{
			Hash: 'FeatureBoard-Decide',
			Template: /*html*/`
<div class="ps-fb-decide">
	<button class="ps-fb-accept" onclick="_Pict.views['Plansheet-FeatureBoard'].decide(event,{~D:Record.IDFeature~},'Accept')">Accept</button>
	<button class="ps-fb-back" onclick="_Pict.views['Plansheet-FeatureBoard'].decide(event,{~D:Record.IDFeature~},'SendBack')">{~D:Record.BackLabel~}</button>
</div>`
		},
		{
			Hash: 'FeatureBoard-Awaiting',
			Template: /*html*/`<div class="ps-fb-awaiting">no acceptance recorded yet</div>`
		},
		{
			Hash: 'FeatureBoard-Pick',
			Template: /*html*/`<input type="checkbox" class="ps-fb-card-check" {~D:Record.CheckedAttr~} onclick="_Pict.views['Plansheet-FeatureBoard'].togglePick(event,{~D:Record.IDFeature~})">`
		},
		{
			Hash: 'FeatureBoard-PickAll',
			Template: /*html*/`<button class="ps-fb-pickall" onclick="_Pict.views['Plansheet-FeatureBoard'].pickAll()">Select all {~D:Record.Pickable~} awaiting a decision</button>`
		},
		{
			Hash: 'FeatureBoard-BulkBar',
			Template: /*html*/`
<div class="ps-fb-bulkbar">
	<span class="ps-fb-bulkcount">{~D:Record.Count~} feature{~D:Record.Plural~} selected</span>
	<button class="ps-fb-accept" onclick="_Pict.views['Plansheet-FeatureBoard'].decideMany('Accept')">Accept all</button>
	<button class="ps-fb-back" onclick="_Pict.views['Plansheet-FeatureBoard'].decideMany('SendBack')">Send all back</button>
	<button class="ps-fb-bulkclear" onclick="_Pict.views['Plansheet-FeatureBoard'].clearPicks()">Clear</button>
</div>`
		}
	],
	Renderables:
	[
		{
			RenderableHash: 'FeatureBoard-Display',
			TemplateHash: 'FeatureBoard-Display',
			ContentDestinationAddress: '#Plansheet-Workspace'
		}
	]
};
